/**
 * One model picker for every place a model is chosen.
 *
 * The Chat page and the Agent dialog used to draw their own — a menu on one, a
 * bare `<select>` on the other — and the moment a second provider arrived the
 * `<select>` was the wrong control: a hundred-odd router models with no
 * description, no context size, and no way to tell whose key they run on.
 *
 * What it shows is *groups*: the service keys as one, then each connection of
 * the person's own. Inside a group the models are labelled by provider when
 * there is more than one, because "GPT-OSS 120B" is on both Groq's list and
 * the router's and the answer to "which one?" is the price and the speed. And
 * it ends with a way to add a key, because the reason to open this list and
 * not find what you want is that the key that has it is not connected yet.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './model-picker.css'

/** What every model needs to be drawn here; both features' model types satisfy it. */
export interface PickerModel {
  model: string
  label: string
  summary: string
  context_window: number | null
  reasoning: boolean
  known: boolean
  /** Which service the model runs on; absent on lists from before it was said. */
  provider?: string | null
  /** True when at least one inference provider serves it for nothing. */
  free?: boolean | null
  served_by?: string[]
}

/** Whose key calls the models: the service's, or one connection. */
export interface PickerGroup {
  key: string
  label: string
  /** A quiet second line on the heading, e.g. "your Groq key". */
  sub?: string | null
  models: PickerModel[]
  error?: string | null
  loading?: boolean
}

/** A connector the footer offers to connect, with where to return afterwards. */
export interface PickerConnector {
  connector_id: string
  label: string
}

/** How a provider id reads to a person. Anything else is shown as its id. */
export const PROVIDER_NAMES: Record<string, string> = {
  groq: 'Groq',
  huggingface: 'Hugging Face',
}

export function providerName(id: string | null | undefined): string {
  return (id && PROVIDER_NAMES[id]) || id || ''
}

/** Search kicks in when the list is long enough that scrolling it is a chore. */
const SEARCH_FROM = 12

export function ModelPicker({
  groups,
  source,
  model,
  onPick,
  connectors,
  returnTo,
  drop = 'up',
  block = false,
  placeholder = 'Choose a model',
}: {
  groups: PickerGroup[]
  source: string
  model: string
  onPick: (key: string, model: string) => void
  connectors: PickerConnector[]
  /** Where "Connect …" comes back to once the key is saved. */
  returnTo?: string
  /** Which way the panel opens. Up from a composer at the foot of the page, down in a form. */
  drop?: 'up' | 'down'
  /** Full width and boxed, like the inputs around it in a form. */
  block?: boolean
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const root = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)
  useDismiss(root, open, () => setOpen(false))

  const current = groups.find((g) => g.key === source)
  const chosen = current?.models.find((m) => m.model === model)
  const several = groups.length > 1
  const total = groups.reduce((n, g) => n + g.models.length, 0)
  const searchable = total >= SEARCH_FROM

  useEffect(() => {
    if (open) {
      setQuery('')
      // Let the panel mount before asking for focus.
      const id = window.setTimeout(() => search.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
  }, [open])

  const needle = query.trim().toLowerCase()
  const visible = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        models: needle
          ? g.models.filter((m) =>
              `${m.label} ${m.model} ${m.summary} ${providerName(m.provider)}`
                .toLowerCase()
                .includes(needle),
            )
          : g.models,
      })),
    [groups, needle],
  )

  return (
    <div className={`mp ${block ? 'mp-block' : ''}`} data-drop={drop} ref={root}>
      <button
        type="button"
        className="mp-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={
          chosen
            ? `${chosen.summary}${several ? ` — via ${current?.label}` : ''}`
            : placeholder
        }
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mp-trigger-text">
          <span className="truncate">{chosen?.label ?? placeholder}</span>
          {chosen && (
            <span className="mp-trigger-sub truncate">
              {[
                providerName(chosen.provider),
                several && current ? current.label : null,
                chosen.context_window ? `${fmtContext(chosen.context_window)} context` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </span>
        <span className="mp-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="mp-panel" role="listbox">
          {searchable && (
            <div className="mp-search">
              <input
                ref={search}
                className="mp-search-input"
                value={query}
                placeholder={`Search ${total} models…`}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOpen(false)
                }}
              />
            </div>
          )}

          {visible.map((g) => (
            <Group
              key={g.key}
              group={g}
              titled={several}
              source={source}
              model={model}
              filtered={Boolean(needle)}
              onPick={(id) => {
                onPick(g.key, id)
                setOpen(false)
              }}
            />
          ))}

          {connectors.length > 0 && (
            <p className="mp-note mp-foot">
              Need a model that is not here?{' '}
              {connectors.map((c, i) => (
                <span key={c.connector_id}>
                  {i > 0 && (i === connectors.length - 1 ? ' or ' : ', ')}
                  <Link
                    to={`/connections/new/${c.connector_id}${
                      returnTo ? `?return=${encodeURIComponent(returnTo)}` : ''
                    }`}
                    onClick={() => setOpen(false)}
                  >
                    Connect {c.label}
                  </Link>
                </span>
              ))}{' '}
              with your own key and its models appear as a group here.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Group({
  group: g,
  titled,
  source,
  model,
  filtered,
  onPick,
}: {
  group: PickerGroup
  titled: boolean
  source: string
  model: string
  filtered: boolean
  onPick: (model: string) => void
}) {
  // Provider sub-headings only when a group actually mixes providers — one
  // heading over one provider is noise.
  const providers = Array.from(new Set(g.models.map((m) => m.provider ?? '')))
  const mixed = providers.length > 1
  const byProvider = mixed
    ? providers.map((p) => ({ id: p, models: g.models.filter((m) => (m.provider ?? '') === p) }))
    : [{ id: '', models: g.models }]

  return (
    <div className="mp-group">
      {titled && (
        <div className="mp-head">
          {g.label}
          {g.sub && <span className="mp-head-sub">{g.sub}</span>}
        </div>
      )}
      {g.loading && <p className="mp-note">Asking which models this key can call…</p>}
      {!g.loading && g.models.length === 0 && (
        <p className="mp-note">
          {filtered ? 'Nothing here matches.' : g.error ?? 'No chat models on this key.'}
        </p>
      )}
      {byProvider.map((section) => (
        <div key={section.id}>
          {mixed && <div className="mp-provider">{providerName(section.id)}</div>}
          {section.models.map((m) => (
            <Row
              key={m.model}
              model={m}
              selected={g.key === source && m.model === model}
              onPick={() => onPick(m.model)}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

function Row({
  model: m,
  selected,
  onPick,
}: {
  model: PickerModel
  selected: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      className="mp-item"
      role="option"
      aria-selected={selected}
      onClick={onPick}
    >
      <span className="mp-itemtext">
        <span>
          <span className="truncate">{m.label}</span>
          {m.reasoning && (
            <span className="mp-badge" title="Thinks before it answers">
              reasons
            </span>
          )}
          {m.free && (
            <span className="mp-badge mp-badge-ok" title="Served free by at least one provider">
              free
            </span>
          )}
        </span>
        <span className="mp-itemsub">
          {m.summary}
          {m.context_window ? ` · ${fmtContext(m.context_window)} context` : ''}
        </span>
      </span>
      {selected && (
        <span className="mp-check" aria-hidden="true">
          ✓
        </span>
      )}
    </button>
  )
}

/** 131072 → "131k", 1048576 → "1.0M", 10000000 → "10M". */
export function fmtContext(tokens: number): string {
  if (tokens >= 1_000_000) {
    const m = tokens / 1_000_000
    return `${m >= 10 ? Math.round(m) : m.toFixed(1)}M`
  }
  return `${Math.round(tokens / 1000)}k`
}

/** Close on outside click and on Escape — a menu you dismiss twice is worse than none. */
export function useDismiss(root: React.RefObject<HTMLElement>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [root, open, close])
}
